import { z } from "zod";

import { findSensitiveField } from "../sensitiveFields";
import {
  type DomainEventName,
  EVENT_CATALOG,
  type EventPayload,
  isDomainEventName,
} from "./catalog";

/**
 * Olay zarfı ve üretici/tüketici sözleşmesi (CLOS-324).
 *
 * Zarf ile payload ayrıdır: zarf "kim, hangi firmada, hangi zincirin parçası olarak, ne
 * zaman" sorularını taşır; payload yalnız olayın kendi verisidir. Karıştırılsalardı, bir
 * gün payload'a `tenantId` adında gerçek bir domain alanı geldiğinde ikisi çakışırdı —
 * kuyruk zarfında (`lib/jobs.ts`) aynı ayrımı aynı gerekçeyle yapıyoruz.
 */

/** Zarfın kendi sürümü — payload şemasının sürümünden AYRIDIR. */
export const EVENT_ENVELOPE_VERSION = 1;

/**
 * Olayın aktörü. Sistem olayında kullanıcı kimliği YOKTUR ve uydurulmaz:
 * "sistem yaptı" demek, "kim yaptı bilmiyoruz" demekten dürüsttür (CLOS-318).
 */
export type EventActor = { type: "user"; id: string } | { type: "system" };

export type EventContext = {
  tenantId: string;
  actor: EventActor;
  /** Zinciri tanımlar ve DEĞİŞMEZ (CLOS-302). */
  correlationId?: string;
  /** Bu olayı doğuran birim. */
  causationId?: string;
};

export type DomainEvent<N extends DomainEventName = DomainEventName> = {
  /**
   * Olayın kendi kimliği. Aynı olayın birden çok hedefe gitmesi mümkün olduğu için
   * gerekli: hedef satırlarını birbirine bağlar ve tekrar korumasının anahtarı olur.
   */
  id: string;
  envelopeVersion: number;
  name: N;
  schemaVersion: number;
  tenantId: string;
  actor: EventActor;
  correlationId: string | null;
  causationId: string | null;
  occurredAt: Date;
  payload: EventPayload<N>;
};

/**
 * Katalog sözleşmesine uyan bir olay üretir. Uymayan her şey **fırlatır** — sessizce
 * düzeltmez.
 *
 * Kusurlu bir olayı maskeleyip yola devam etmek, hatayı üreticiden alıp tüketiciye
 * taşır: sorun artık onu çıkaran kodun yanında değil, günler sonra başka bir modülde
 * görünür.
 */
export function buildEvent<N extends DomainEventName>(
  name: N,
  ctx: EventContext,
  payload: EventPayload<N>,
): DomainEvent<N> {
  const tanim = EVENT_CATALOG[name];
  if (!tanim) throw new Error(`Olay katalogda yok: ${String(name)}`);

  if (ctx.actor.type !== tanim.actor) {
    throw new Error(
      `Olay "${name}" ${tanim.actor} aktörü ister, ${ctx.actor.type} verildi (aktör uyuşmuyor)`,
    );
  }
  if (ctx.actor.type === "user" && !ctx.actor.id) {
    throw new Error(`Olay "${name}" gerçek aktör ister; aktör kimliği boş olamaz`);
  }

  // Şema strict olduğu için yasak alan zaten "bilinmeyen alan" olarak düşerdi. Bu
  // ikinci katman, bir gün bir şemaya yanlışlıkla böyle bir alan eklenirse durdurur —
  // ve maskeleyerek değil, İHBAR EDEREK: sessiz maskeleme kusuru görünmez kılar.
  const hassas = findSensitiveField(payload);
  if (hassas) {
    throw new Error(`Olay "${name}" payload'ında hassas alan var: ${hassas}`);
  }

  const sonuc = tanim.payload.safeParse(payload);
  if (!sonuc.success) {
    throw new Error(`Olay "${name}" payload sözleşmesine uymuyor: ${sonuc.error.message}`);
  }

  return {
    id: crypto.randomUUID(),
    envelopeVersion: EVENT_ENVELOPE_VERSION,
    name,
    schemaVersion: tanim.version,
    tenantId: ctx.tenantId,
    actor: ctx.actor,
    correlationId: ctx.correlationId ?? null,
    causationId: ctx.causationId ?? null,
    occurredAt: new Date(),
    payload: sonuc.data as EventPayload<N>,
  };
}

const ZARF_SEMASI = z.object({
  id: z.uuid(),
  envelopeVersion: z.number().int(),
  name: z.string(),
  schemaVersion: z.number().int(),
  tenantId: z.uuid(),
  actor: z.union([
    z.object({ type: z.literal("user"), id: z.uuid() }),
    z.object({ type: z.literal("system") }),
  ]),
  correlationId: z.string().nullable(),
  causationId: z.string().nullable(),
  occurredAt: z.coerce.date(),
  payload: z.unknown(),
});

/**
 * Tüketici tarafı: dışarıdan gelen bir olayı AYNI katalogla doğrular.
 *
 * Sürüm uyuşmazlığı sessizce kabul edilmez. Tanımadığı bir sürümü "anlamış gibi"
 * işleyen tüketici, en sinsi veri bozulmasını üretir: hata vermez, yanlış sonuç verir.
 */
export function parseEvent(input: unknown): DomainEvent {
  const zarf = ZARF_SEMASI.safeParse(input);
  if (!zarf.success) throw new Error(`Olay zarfı geçersiz: ${zarf.error.message}`);

  const { name, schemaVersion, envelopeVersion } = zarf.data;
  if (!isDomainEventName(name)) throw new Error(`Olay katalogda yok: ${name}`);
  if (envelopeVersion !== EVENT_ENVELOPE_VERSION) {
    throw new Error(`Bilinmeyen zarf sürümü: ${envelopeVersion}`);
  }

  const tanim = EVENT_CATALOG[name];
  if (schemaVersion !== tanim.version) {
    throw new Error(
      `Olay "${name}" için bilinmeyen şema sürümü: ${schemaVersion} (beklenen ${tanim.version})`,
    );
  }

  const sonuc = tanim.payload.safeParse(zarf.data.payload);
  if (!sonuc.success) {
    throw new Error(`Olay "${name}" payload sözleşmesine uymuyor: ${sonuc.error.message}`);
  }

  return { ...zarf.data, name, payload: sonuc.data } as DomainEvent;
}
